import { useState } from 'react';
import { Check, ChevronDown, CornerDownRight, Loader2, Trash2, Wrench } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { ImageLightbox } from './ImageLightbox';
import { proposalViewSrc, type MeshProposal } from './threeDApi';

// ONE PROPOSAL, and every one this mesh has had.
//
// It was a single panel showing the last answer, in React state: a refresh lost it, and so
// did closing the viewer. What that threw away was not a cache — a proposal is a paid call
// to a strong model and half a minute of waiting, and the ones NOBODY APPLIED are the half
// worth keeping most. You asked, you read the code, you were not sure. Asking again is the
// only way back, and it costs the same again.
//
// So they are rows now, newest first, and this draws one. The newest opens; the rest are a
// line each until pressed, because five open cards with their code showing is a page of
// Blender under a viewer.
//
// THE RENDERS ARE KEPT, on every row and not just the newest. They used to be the live
// answer's alone, held in React state, on the argument that three PNGs a row is too much to
// store — which measured them against nothing. They are three 512-pixel pictures of one
// part on white, in a database that keeps a multi-megabyte mesh for every run.
//
// And they are the evidence. The reasoning below is written ABOUT these pictures — which
// end is short of a full disc, where the thread stops — while the mesh above has usually
// been edited since. A proposal whose renders are gone is a paragraph of argument about
// something nobody can look at any more, which is exactly what "come back to it later" was
// supposed to protect.
//
// A proposal from before they were kept simply has none, and shows none.
export function MeshProposalCard({
  proposal, sawDrawing, busy, open: openAt, onApply, onRefine, onForget,
}: {
  proposal: MeshProposal;
  // Whether that call had a drawing to compare against. Undefined on history, where it is
  // not recorded — the sentence and the code are, and those are what it left behind.
  sawDrawing?: boolean;
  busy: boolean;
  open?: boolean;
  onApply: () => void;
  // Ask for a change that BUILDS ON this one — a refinement, a correction, the next step.
  onRefine: () => void;
  onForget: () => void;
}) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(!!openAt);
  // Which render is zoomed, by its URL (null = none). The lightbox steps through the set,
  // which is the only way to compare two of them: at 48 pixels a thumbnail says which view
  // it is and nothing about what is on it.
  const [zoom, setZoom] = useState<string | null>(null);
  const p = proposal;
  const applied = !!p.appliedAt;
  const views = p.views || [];
  const viewSrcs = views.map((v) => proposalViewSrc(v.id));

  return (
    <div className="flex w-full items-start gap-2 rounded-lg border border-slate-300 p-2 text-left">
      {views.length > 0 && (
        <div className="flex shrink-0 gap-1">
          {views.map((v) => (
            <button
              key={v.id}
              type="button"
              // Clicking ZOOMS, exactly as it does on the same three renders in the ask
              // modal. There is nothing else a click on them could mean — they cannot be
              // chosen or unchosen — and a closer look is the whole reason they are here.
              onClick={() => setZoom(proposalViewSrc(v.id))}
              title={t('notice.3d.sawThis')}
              className="rounded border border-slate-300 bg-white transition-opacity hover:opacity-80"
            >
              <img
                src={proposalViewSrc(v.id)}
                alt={v.name}
                className="h-12 w-12 rounded object-contain"
              />
            </button>
          ))}
        </div>
      )}

      <ImageLightbox
        src={zoom}
        srcs={viewSrcs}
        onNavigate={setZoom}
        onClose={() => setZoom(null)}
      />


      {/* The prose centres; the CODE does not. A proposal is two sentences and a
          confidence, and centred they read as a caption to the renders beside them —
          which is what they are. Code centred is code nobody can read: its indentation is
          its structure. */}
      <div className="min-w-0 flex-1 text-center">
        {/* THE NAME FIRST, then the sentence under it. It was the other way round, which
            put three lines of somebody's own words above the one line saying what came
            back — a title should be the first thing a row says, and the request is what
            that title is an answer to. */}
        {p.op ? (
          <p className="text-[12.5px] font-medium text-slate-600">
            {p.name || t('notice.3d.transformation')}
            {applied && (
              <span className="ml-1.5 inline-flex items-center gap-0.5 text-[11px] font-normal text-blue-300">
                <Check className="h-3 w-3" />
                {t('notice.3d.wasApplied')}
              </span>
            )}
          </p>
        ) : (
          <p className="text-[12.5px] font-medium text-amber-300">{t('notice.3d.noProposal')}</p>
        )}
        {/* WHAT WAS ASKED. On the live answer it is still in the box below; on a row from
            last week it is the only thing that says what this code was for. */}
        {p.instruction && (
          <p className="mt-0.5 text-[11px] italic leading-snug text-slate-400">
            “{p.instruction}”
          </p>
        )}

        {/* A button, and it says only what it does. It carried the transformation's name
            for a moment — but the name is the line above it, and a row that says "cut
            second transverse hole" twice reads as two different things. */}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="mt-1 inline-flex items-center gap-1 rounded-lg border border-slate-300 px-2 py-0.5 text-[10.5px] font-medium text-slate-500 transition-colors hover:border-blue-400 hover:text-blue-600"
        >
          {t(open ? 'notice.3d.proposalLess' : 'notice.3d.proposalMore')}
          <ChevronDown className={`h-3 w-3 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>

        {open && (
          <>
            {p.reason && (
              <p className="mt-0.5 text-[11px] leading-snug text-slate-600">{p.reason}</p>
            )}
            {/* WHAT WILL ACTUALLY RUN. The only honest way to offer code is to show it.
                Scrollable and small: it is here to be checked before pressing, not to be
                read every time. */}
            {p.code && (
              <pre className="mt-1 max-h-32 overflow-auto whitespace-pre rounded border border-slate-200 bg-slate-50 p-1.5 text-left text-[10px] leading-snug text-slate-600">
                {p.code}
              </pre>
            )}
            {p.confidence && (
              <p className="mt-0.5 text-[11px] text-slate-600">
                {t(`notice.3d.conf${p.confidence[0].toUpperCase()}${p.confidence.slice(1)}`)}
                {' '}
                {t('notice.3d.confidence')}
              </p>
            )}
            {sawDrawing === false && (
              <p className="mt-0.5 text-[11px] text-amber-300">{t('notice.3d.noDrawing')}</p>
            )}
            {/* Whatever the server would not pass on. Amber and not hidden: each one means
                the answer was not usable as written. */}
            {p.warnings.map((w) => (
              <p key={w} className="mt-0.5 text-[11px] text-amber-300">{w}</p>
            ))}
          </>
        )}
      </div>

      <div className="flex shrink-0 flex-col items-center gap-1 self-center">
        {p.op && (
          <button
            type="button"
            onClick={onApply}
            disabled={busy}
            // APPLIED IS NOT SPENT. The same code run twice cuts twice, which is a real
            // thing to want (two holes, two chamfers) and a real thing to do by accident —
            // so it says it has been applied and stays pressable.
            className={`${'inline-flex shrink-0 items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[12.5px] font-medium transition-colors disabled:cursor-not-allowed'} border-blue-400 text-blue-300 hover:bg-blue-400/10`}
          >
            {busy
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <Wrench className="h-3.5 w-3.5" />}
            {t(applied ? 'notice.3d.applyAgain' : 'notice.3d.applyProposal')}
          </button>
        )}
        {/* THE OTHER KIND OF NEXT QUESTION. "Describe a change" asks for something else;
            this asks for something about THIS — thread the hole it cut, make it 2 mm
            deeper, put it on the other end. The difference is not in the wording of the
            sentence, it is a thing somebody knows and can say in one press, and the model
            is handed this transformation's code as what is being corrected. */}
        {p.op && (
          <button
            type="button"
            onClick={onRefine}
            title={t('notice.3d.refineHint')}
            // THE SAME TYPE AS "Apply this" ABOVE IT. They are two answers to the same
            // question — do this, or ask for something more — and a smaller font made the
            // second read as a footnote to the first rather than as its alternative.
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-2 py-1 text-[12.5px] font-medium text-slate-600 transition-colors hover:border-blue-400 hover:text-blue-600"
          >
            <CornerDownRight className="h-3.5 w-3.5" />
            {t('notice.3d.refine')}
          </button>
        )}
        <button
          type="button"
          onClick={onForget}
          title={t('notice.3d.forgetProposal')}
          aria-label={t('notice.3d.forgetProposal')}
          className="rounded-lg p-1 text-slate-400 transition-colors hover:text-rose-600"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
